import { Grid, Paper, Tooltip, Typography, WithStyles } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { styles } from "./PersonalDataView.styles";
import { useTranslation } from "react-i18next";
import { FormTextField } from "../../components/FormComponents/FormTextField";
import { FormNativeSelect } from "../../components/FormComponents/FormNativeSelect";
import { BlueButton } from "../../components/Buttons/BlueButton";
import { emailPattern, phonePattern } from "../../../utilities/generalUtils";
import { useHistory } from "react-router-dom";
import { selectVisibleCountries } from "../../../selectors/countries/CountriesSelector";

export interface IPersonalDetailsFormProps extends WithStyles<typeof styles> {
    userDetails: {
        email: string,
        additionalEmail2: string,
        additionalEmail2Verified: boolean,
        additionalEmail3: string,
        additionalEmail3Verified: boolean,
        firstName: string,
        lastName: string,
        phone: any,
        mobilePhone: any,
        billingAddress: any,
        deliveryAddress: any,
        company: string,
        tin: string,
        userName: string
    };
    onSave: any;
    fromCheckout?: boolean;
}

const PersonalDetailsFormPure: React.FunctionComponent<IPersonalDetailsFormProps> = ({
                                                                                         onSave,
                                                                                         fromCheckout,
                                                                                         userDetails,
                                                                                         classes
                                                                                     }: IPersonalDetailsFormProps) => {

    const {t} = useTranslation();
    const countries = useSelector(state => selectVisibleCountries(state));
    const history = useHistory();    
    const internalUser = userDetails.userName === "";

    let deliveryName = "";
    if (userDetails.deliveryAddress.name) {
        deliveryName = userDetails.deliveryAddress.name.split(" ");
    }

    const [details, setDetails] = React.useState({
        email: userDetails.email,
        additionalEmail2: userDetails.additionalEmail2 || "",
        additionalEmail3: userDetails.additionalEmail3 || "",
        additionalEmail2Verified: userDetails.additionalEmail2Verified,
        additionalEmail3Verified: userDetails.additionalEmail3Verified,
        firstName: userDetails.firstName,
        lastName: userDetails.lastName,
        phone: userDetails.phone,
        mobilePhone: userDetails.mobilePhone,
        company: userDetails.company,
        tin: userDetails.tin,

        billingAddressLine1: userDetails.billingAddress.addressLine1,
        billingAddressLine2: userDetails.billingAddress.addressLine2,
        billingPostcode: userDetails.billingAddress.postcode,
        billingCity: userDetails.billingAddress.city,
        billingCountry: userDetails.billingAddress.country,

        deliveryFirstName: deliveryName?.[0] || "",
        deliveryLastName: deliveryName?.[deliveryName.length - 1] || "",
        deliveryAddressLine1: userDetails.deliveryAddress.addressLine1 || "",
        deliveryAddressLine2: userDetails.deliveryAddress.addressLine2 || "",
        deliveryPostcode: userDetails.deliveryAddress.postcode || "",
        deliveryCity: userDetails.deliveryAddress.city || "",
        deliveryCountry: userDetails.deliveryAddress.country || ""
    });

    const [errors, setErrors] = useState([]);
    const handleChange = (event, valid) => {
        const name = event.target.name;
        const value = event.target.value;
        setDetails({
            ...details,
            [name]: value
        });

        if (valid) {
            setErrors(errors.filter(e => e !== name));
        } else {
            setErrors(errors.concat(name));
        }
    };

    const handleCountry = (event) => {
        if (event.target.value !== "") {
            setDetails({
                ...details,
                [event.target.name]: event.target.value
            });
        }
    }

    const copyBillingAddress = (event) => {
        event.preventDefault();
        setDetails({
            ...details,
            deliveryFirstName: details.firstName,
            deliveryLastName: details.lastName,
            deliveryAddressLine1: details.billingAddressLine1,
            deliveryAddressLine2: details.billingAddressLine2,
            deliveryCity: details.billingCity,
            deliveryPostcode: details.billingPostcode,
            deliveryCountry: details.billingCountry
        });
    };

    const isFormValid = () => {
        return !errors.length
            && details.firstName
            && details.lastName
            && details.company
            && details.billingAddressLine1
            && details.billingCity
            && details.billingCountry
            && details.billingPostcode;
    };

    const goBackToBasket = () => {
        history.push("/basket/2");
    };

    const updateUser = (event) => {

        event.preventDefault();

        userDetails.firstName = details.firstName;
        userDetails.lastName = details.lastName;
        userDetails.phone = details.phone;
        userDetails.mobilePhone = details.mobilePhone;
        userDetails.company = details.company;
        userDetails.tin = details.tin;
        userDetails.additionalEmail2 = details.additionalEmail2;
        userDetails.additionalEmail3 = details.additionalEmail3;

        userDetails.billingAddress.name = details.firstName + " " + details.lastName;
        userDetails.billingAddress.addressLine1 = details.billingAddressLine1;
        userDetails.billingAddress.addressLine2 = details.billingAddressLine2;
        userDetails.billingAddress.postcode = details.billingPostcode;
        userDetails.billingAddress.city = details.billingCity;
        userDetails.billingAddress.country = details.billingCountry;

        userDetails.deliveryAddress.name = details.deliveryFirstName + " " + details.deliveryLastName;
        userDetails.deliveryAddress.addressLine1 = details.deliveryAddressLine1;
        userDetails.deliveryAddress.addressLine2 = details.deliveryAddressLine2;
        userDetails.deliveryAddress.postcode = details.deliveryPostcode;
        userDetails.deliveryAddress.city = details.deliveryCity;
        userDetails.deliveryAddress.country = details.deliveryCountry;

        onSave(userDetails);
    };

    const email2HelperText = userDetails.additionalEmail2Verified === true ? "" : "Email must be validated";
    const email3HelperText = userDetails.additionalEmail3Verified === true ? "" : "Email must be validated";

    return <>
        <form autoComplete="on">
            <Paper className={classes.formBox}>
                <Typography variant="h3"
                            className={classes.formTitle}>User's details</Typography>
                <Grid container spacing={3} direction="row">
                    <Grid item xs={12} sm={4}>
                        <FormTextField id="firstName" error={details.firstName === ""} required onChange={handleChange}
                                       value={details.firstName} label={t("general.firstName")} disabled={internalUser} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormTextField id="lastName" error={details.lastName === ""} required onChange={handleChange}
                                       value={details.lastName} label={t("general.lastName")} disabled={internalUser} />
                    </Grid>
                    <Grid item xs={12} sm={4}>

                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Tooltip title="Cannot edit primary email">
                            <span>
                                <FormTextField id="primary_email" error={details.firstName === ""} onChange={handleChange}
                                               value={details.email} label="Primary email" disabled={true} />
                            </span>
                        </Tooltip>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormTextField id="additionalEmail2" error={details.lastName === ""} helperText={email2HelperText}
                                       onChange={handleChange}
                                       value={details.additionalEmail2} label="Email 2" pattern={emailPattern} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormTextField id="additionalEmail3" error={details.lastName === ""} helperText={email3HelperText}
                                       onChange={handleChange}
                                       value={details.additionalEmail3} label="Email 3" pattern={emailPattern} />
                    </Grid>

                </Grid>
                <BlueButton
                    disabled={!isFormValid()}
                    onClick={updateUser}
                >{t("manageAccount.personalData.updateButton")}
                </BlueButton>
            </Paper>

            <Paper className={classes.formBox}>
                <Typography variant="h3"
                            className={classes.formTitle}>{t("manageAccount.personalData.billingInformation")}</Typography>
                <Grid container spacing={3} direction="row" alignItems="center">
                    <Grid item xs={12} sm={6}>
                        <FormTextField id="billingAddressLine1" error={details.billingAddressLine1 === ""} required
                                       onChange={handleChange} value={details.billingAddressLine1}
                                       label={t("manageAccount.personalData.addressLine1")} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormTextField id="billingAddressLine2" onChange={handleChange} value={details.billingAddressLine2}
                                       label={t("manageAccount.personalData.addressLine2")} />
                    </Grid>
                </Grid>
                <Grid container spacing={3} direction="row" alignItems="center">
                    <Grid item xs={12} sm={4}>
                        <FormTextField id="billingPostcode" error={details.billingPostcode === ""} required
                                       onChange={handleChange} value={details.billingPostcode}
                                       label={t("general.postcode")} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormTextField id="billingCity" error={details.billingCity === ""} required
                                       onChange={handleChange} value={details.billingCity}
                                       label={t("general.city")} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormNativeSelect label={t("general.country")} required options={countries} onChange={handleCountry} name="billingCountry" value={details.billingCountry} emptyCaption={"Select country"}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormTextField id="phone" onChange={handleChange} value={details.phone}
                                       label={t("general.phone")} pattern={phonePattern} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormTextField id="mobilePhone" onChange={handleChange} value={details.mobilePhone}
                                       label={t("general.mobilePhone")} pattern={phonePattern} />
                    </Grid>
                </Grid>
                <Grid container spacing={3} direction="row" alignItems="center">
                    <Grid item xs={12} sm={6}>
                        <FormTextField id="company" error={details.company === ""} required
                                       onChange={handleChange} value={details.company}
                                       label={t("general.company")} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormTextField id="tin"
                                       onChange={handleChange} value={details.tin}
                                       label={t("manageAccount.personalData.taxId")} />
                    </Grid>
                </Grid>
                <Typography variant="body2">{t("manageAccount.personalData.explanation")}</Typography>
                <br></br>
                <BlueButton
                    disabled={!isFormValid()}
                    onClick={updateUser}
                >{t("manageAccount.personalData.updateButton")}
                </BlueButton>
                {fromCheckout && <BlueButton onClick={goBackToBasket}>Back to Basket</BlueButton>}
            </Paper>


            <Paper className={classes.formBox}>

                <Typography variant="h3"
                            className={classes.formTitle}>{t("manageAccount.personalData.deliveryInformation")}</Typography>
                <Grid container spacing={3} direction="row" alignItems="center">
                    <Grid item xs={12} sm={4}>
                        <FormTextField id="deliveryFirstName" onChange={handleChange}
                                       value={details.deliveryFirstName} label={t("general.firstName")} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormTextField id="deliveryLastName" onChange={handleChange} value={details.deliveryLastName}
                                       label={t("general.lastName")} />
                    </Grid>
                </Grid>
                <Grid container spacing={3} direction="row" alignItems="center">
                    <Grid item xs={12} sm={6}>
                        <FormTextField id="deliveryAddressLine1" onChange={handleChange}
                                       value={details.deliveryAddressLine1}
                                       label={t("manageAccount.personalData.addressLine1")} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormTextField id="deliveryAddressLine2" onChange={handleChange}
                                       value={details.deliveryAddressLine2}
                                       label={t("manageAccount.personalData.addressLine2")} />
                    </Grid>
                </Grid>
                <Grid container spacing={3} direction="row" alignItems="center">
                    <Grid item xs={12} sm={4}>
                        <FormTextField id="deliveryPostcode" onChange={handleChange}
                                       value={details.deliveryPostcode} label={t("general.postcode")} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormTextField id="deliveryCity" onChange={handleChange}
                                       value={details.deliveryCity} label={t("general.city")} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormNativeSelect label={t("general.country")} options={countries} onChange={handleCountry}
                                          name="deliveryCountry" value={details.deliveryCountry}
                                          emptyCaption={"Select country"}
                        />
                    </Grid>
                </Grid>
                <BlueButton
                    disabled={!isFormValid()}
                    onClick={updateUser}
                >{t("manageAccount.personalData.updateButton")}
                </BlueButton>
                <BlueButton
                    onClick={copyBillingAddress}
                >{t("manageAccount.personalData.copyAddressButton")}
                </BlueButton>
                {fromCheckout && <BlueButton onClick={goBackToBasket}>Back to Basket</BlueButton>}
            </Paper>

        </form>
    </>;
};

export const PersonalDetailsForm = withStyles(styles)(PersonalDetailsFormPure);
